import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import {
  CardView,
  Card,
  CardPreview,
  Content,
  Text,
  Image,
  Skeleton,
  Header,
  Heading,
  TextField,
  ButtonGroup,
  Button,
  ActionButtonGroup,
  ActionButton,
  LinkButton,
  Link,
  IllustratedMessage,
  TooltipTrigger,
  Tooltip,
  Divider,
  Disclosure,
  DisclosureTitle,
  DisclosurePanel,
  DisclosureHeader,
  DialogTrigger,
} from "@react-spectrum/s2";
import EditIcon from "@react-spectrum/s2/icons/Edit";
import DownloadIcon from "@react-spectrum/s2/icons/Download";
import BugIcon from "@react-spectrum/s2/icons/Bug";
import FeedbackIcon from "@react-spectrum/s2/icons/Feedback";
import ExpressIcon from "icon:@app/assets/AdobeExpressIcon.svg";

import { S3Asset } from "@app/components/S3Asset";
import { HoverState } from "@app/components/HoverState";
import { HeaderActions } from "@app/components/Header";
import { useBreadcrumbs } from "@app/components/Breadcrumbs";
import { UserFeedback } from "@app/components/UserFeedback";
import { EditVideoDialog } from "./EditVideoDialog";

import {
  useVideo,
  useUpdateVideo,
  useVideoTemplate,
  useVideoAssets,
  useDownloadAsset,
  useDownloadAllAssets,
} from "@app/hooks/videos";
import { useUpdateAssets } from "@app/hooks/videoWizard";
import { usePresignURL } from "@app/hooks/s3";
import { useUserProfile } from "@app/hooks/useIMS";
import { useHasRole, useIsDevMode } from "@app/hooks/useRoles";
import { useVideoFeedback } from "@app/hooks/feedback";
import { WizardDemoBoundary } from "./DemoVideoWizard";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

export const ProjectView = () => (
  <WizardDemoBoundary>
    <EditableProjectView />
  </WizardDemoBoundary>
);

const EditableProjectView = () => {
  const params = useParams();
  const navigate = useNavigate();

  const video = useVideo(params?.id);
  const template = useVideoTemplate(video.data?.templateId);

  useBreadcrumbs([
    { label: "Projects", href: "/projects" },
    {
      label: video.data?.name ?? "Project",
      href: `/projects/${params?.id}`,
      isLoading: video.isLoading || !video.data?.name,
    },
  ]);
  const updateVideo = useUpdateVideo(video.data?.id);
  const updateAssets = useUpdateAssets(video.data?.id);
  const videoUrlQuery = usePresignURL(video.data?.videoPath);

  const user = useUserProfile();
  const isVideoEditor = useHasRole("video-editor");
  const isDevMode = useIsDevMode();

  const feedback = useVideoFeedback();

  const isAuthorized = useMemo(() => {
    if (!user?.email || !video.isSuccess) return false;
    const authors = Array.from(
      new Set([
        video.data?.createdBy,
        video.data?.updatedBy,
        ...(video.data?.sharedWith ?? []),
      ]),
    ).filter(Boolean);
    return authors.includes(user.email) || isVideoEditor;
  }, [
    user?.email,
    isVideoEditor,
    video.isSuccess,
    video.data?.createdBy,
    video.data?.updatedBy,
    video.data?.sharedWith,
  ]);

  const selectedCampaign = useMemo(
    () =>
      video.data?.campaigns?.find(
        (campaign) => campaign.id === video.data?.selectedCampaignIds?.[0],
      ),
    [video.data?.campaigns, video.data?.selectedCampaignIds],
  );

  const { assets, isLoading: isAssetsLoading } = useVideoAssets(params?.id);

  const expressDocumentId =
    video.data?.templateAssetsRenditions?.[0]?.documentId;

  const [shouldEditTitle, setShouldEditTitle] = useState(false);

  const handleTitleChange = useCallback(
    (value) => {
      const title = value.trim();

      if (!title || title === video.data?.name) return;

      updateVideo.mutate({ name: title });
    },
    [updateVideo.mutate, video.data?.name],
  );

  const downloadAsset = useDownloadAsset();
  const downloadAllAssets = useDownloadAllAssets(params?.id);

  useEffect(() => {
    if (video.isError) {
      navigate("/projects");
    }
  }, [video.isError, navigate]);

  return (
    <>
      <title>{`${video.data?.name ?? "Project"} | BeStory`}</title>

      {video.data?.templateType === "modular" && (
        <HeaderActions>
          <Button
            variant="primary"
            onPress={feedback.open}
            styles={style({ marginStart: "auto" })}
          >
            <FeedbackIcon />
            <Text>Share feedback</Text>
          </Button>
        </HeaderActions>
      )}

      <UserFeedback
        video={video.data}
        isOpen={feedback.isOpen}
        onClose={feedback.close}
        currentStep="video-view"
        method={feedback.method}
      />

      <div
        className={style({
          maxWidth: 1200,
          marginX: "auto",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "start",
          gap: 64,
        })}
      >
        <Skeleton isLoading={video.isLoading}>
          <div
            className={style({
              display: "grid",
              maxWidth: 1000,
              gridTemplateAreas: {
                isDetailsVisible: [
                  ". .",
                  "header header",
                  ". .",
                  "video details",
                  "actions .",
                ],
                default: [".", "header", ".", "video", "actions"],
              },
              gridTemplateRows: [16, "auto", 0, "auto", "auto"],
              columnGap: 48,
              rowGap: 32,
            })({ isDetailsVisible: video.data?.templateType === "express" })}
          >
            <Header
              styles={style({
                gridArea: "header",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
              })}
            >
              <Heading
                level={1}
                styles={style({
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  font: "heading",
                  margin: 0,
                })}
              >
                {shouldEditTitle ? (
                  <TextField
                    autoFocus
                    defaultValue={video.data?.name}
                    onBlur={(e) => {
                      setShouldEditTitle(false);
                      handleTitleChange(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setShouldEditTitle(false);
                        handleTitleChange(e.target.value);
                      } else if (e.key === "Escape") {
                        setShouldEditTitle(false);
                      }
                    }}
                    styles={style({ width: 300, marginBottom: 2 })}
                  />
                ) : (
                  <>
                    <Text styles={style({ marginY: 4 })}>
                      {video.data?.name}
                    </Text>
                    {isAuthorized && (
                      <ActionButton
                        aria-label="Edit title"
                        size="S"
                        isQuiet
                        isDisabled={video.isLoading}
                        onPress={() => setShouldEditTitle(!shouldEditTitle)}
                        styles={style({ marginEnd: -28 })}
                      >
                        <EditIcon />
                      </ActionButton>
                    )}
                  </>
                )}
              </Heading>
              <Text styles={style({ font: "detail" })}>
                {new Date(Date.parse(video.data?.updatedAt)).toLocaleString(
                  "en-US",
                  {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                )}
              </Text>
              {isDevMode && (
                <ActionButtonGroup>
                  {video.data?.assets.some((asset) => asset.name === "VO1") && (
                    <ActionButton
                      size="S"
                      isQuiet
                      onPress={() => {
                        updateAssets.mutate(
                          {
                            toRemove: [{ name: "VO1" }, { name: "VO1-V1" }],
                          },
                          {
                            onSuccess: () => {
                              updateVideo.mutate(
                                {
                                  videoPath: null,
                                },
                                {
                                  onSuccess: () => {
                                    navigate(`/projects/new/${video.data?.id}`);
                                  },
                                },
                              );
                            },
                          },
                        );
                      }}
                    >
                      <BugIcon />
                      <Text>Edit Voiceover</Text>
                    </ActionButton>
                  )}
                </ActionButtonGroup>
              )}
            </Header>

            <div
              className={style({
                gridArea: "video",
              })}
            >
              {videoUrlQuery.isSuccess ? (
                <video
                  src={videoUrlQuery.data?.presignURL}
                  controls
                  className={style({
                    display: "block",
                    width: 1000,
                    maxWidth: "full",
                    height: "full",
                    aspectRatio: "16 / 9",
                    borderRadius: "default",
                    backgroundColor: "gray-200",
                  })}
                />
              ) : (
                <Skeleton isLoading={true}>
                  <Image
                    alt=""
                    styles={style({
                      size: "full",
                      aspectRatio: "16 / 9",
                      borderRadius: "default",
                    })}
                  />
                </Skeleton>
              )}
            </div>

            {video.data?.templateType === "express" && (
              <Card styles={style({ gridArea: "details" })}>
                <Content
                  styles={style({
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  })}
                >
                  <div>
                    <Text slot="description">Customer name</Text>
                    <Text slot="title">{video.data?.customer?.name}</Text>
                  </div>
                  {!!video.data?.drNumber && (
                    <div>
                      <Text slot="description">DR number</Text>
                      <Text slot="title">{video.data?.drNumber}</Text>
                    </div>
                  )}
                  {!!video.data?.accountName && (
                    <div>
                      <Text slot="description">Account name</Text>
                      <Text slot="title">{video.data?.accountName}</Text>
                    </div>
                  )}
                  <div>
                    <Text slot="description">Industry</Text>
                    <Text slot="title">{video.data?.customer?.industry}</Text>
                  </div>
                  <div>
                    <Text slot="description">Website</Text>
                    <Text slot="title">
                      {video.data?.customer?.websiteURL ? (
                        <Link
                          href={video.data.customer.websiteURL}
                          target="_blank"
                          staticColor="auto"
                          UNSAFE_className={style({
                            fontWeight: "normal",
                            wordBreak: "normal",
                            overflowWrap: "anywhere",
                          })}
                        >
                          {video.data.customer.websiteURL}
                        </Link>
                      ) : (
                        "\u2013"
                      )}
                    </Text>
                  </div>
                  <div>
                    <Text slot="description">Brand center</Text>
                    <Text slot="title">
                      {video.data?.customer?.brandCenterURL ? (
                        <Link
                          href={video.data.customer.brandCenterURL}
                          target="_blank"
                          staticColor="auto"
                          UNSAFE_className={style({
                            fontWeight: "normal",
                            wordBreak: "normal",
                            overflowWrap: "anywhere",
                          })}
                        >
                          {video.data.customer.brandCenterURL}
                        </Link>
                      ) : (
                        "\u2013"
                      )}
                    </Text>
                  </div>
                  <div>
                    <Text slot="description">Campaign focus</Text>
                    <Text slot="title">
                      {selectedCampaign?.tagline ?? "\u2013"}
                    </Text>
                  </div>
                  <div>
                    <Text slot="description">Campaign website</Text>
                    <Text slot="title">
                      {selectedCampaign?.landingPageURL ? (
                        <Link
                          href={selectedCampaign?.landingPageURL}
                          target="_blank"
                          staticColor="auto"
                          UNSAFE_className={style({
                            fontWeight: "normal",
                            wordBreak: "normal",
                            overflowWrap: "anywhere",
                          })}
                        >
                          {selectedCampaign?.landingPageURL}
                        </Link>
                      ) : (
                        "\u2013"
                      )}
                    </Text>
                  </div>
                </Content>
              </Card>
            )}

            <ButtonGroup
              size="L"
              styles={style({ gridArea: "actions", marginX: "auto" })}
            >
              {isAuthorized && (
                <DialogTrigger>
                  <Button variant="secondary">
                    <Text>Edit</Text>
                  </Button>
                  <EditVideoDialog videoId={video.data?.id} />
                </DialogTrigger>
              )}
              {video.data?.templateType === "express" && (
                <LinkButton
                  href={`https://new.express.adobe.com/id/${video.data?.expressId}?category=none`}
                  target="_blank"
                  isDisabled={
                    video.isLoading || video.data?.templateType !== "express"
                  }
                  variant="secondary"
                  fillStyle="outline"
                >
                  <ExpressIcon />
                  <Text>Open in Express</Text>
                </LinkButton>
              )}
              <LinkButton
                href={videoUrlQuery.data?.presignURL || null}
                download
                isDisabled={videoUrlQuery.isLoading}
                variant="accent"
              >
                <DownloadIcon />
                <Text>Download video</Text>
              </LinkButton>
            </ButtonGroup>
          </div>

          {video.data?.id && (
            <UserFeedback
              variant="inline"
              title="How did your video turn out?"
              video={video.data}
              currentStep="video-view"
              method="inline"
              styles={style({ width: "full", maxWidth: 400 })}
            />
          )}

          <IllustratedMessage size="S">
            <Heading>Do you need help with your project?</Heading>
            <ButtonGroup size="S">
              <LinkButton
                variant="secondary"
                href="https://adobe.ly/ExperienceDesign-Request"
                target="_blank"
              >
                <Text>Request design assist</Text>
              </LinkButton>
            </ButtonGroup>
          </IllustratedMessage>

          <Divider />

          <Disclosure
            size="XL"
            isQuiet
            styles={style({ width: "full", marginBottom: 80 })}
          >
            <DisclosureHeader
              styles={style({
                width: "full",
                // font: "heading",
                // display: "flex",
                // alignItems: "center",
                // gap: 8,
              })}
            >
              <DisclosureTitle>Assets</DisclosureTitle>
              <div className={style({ width: 12 })} />
              <ButtonGroup styles={style({ marginStart: "auto" })}>
                {!!template.data?.assetsTemplateId && (
                  <LinkButton
                    href={
                      expressDocumentId
                        ? `https://new.express.adobe.com/id/${expressDocumentId}?category=none`
                        : undefined
                    }
                    target="_blank"
                    variant="secondary"
                    fillStyle="outline"
                    isDisabled={!expressDocumentId}
                  >
                    <ExpressIcon />
                    <Text>Open in Express</Text>
                  </LinkButton>
                )}
                <Button
                  variant="secondary"
                  onPress={() => downloadAllAssets.mutate()}
                  isPending={downloadAllAssets.isPending}
                  isDisabled={isAssetsLoading}
                >
                  <DownloadIcon />
                  <Text>Download all</Text>
                </Button>
              </ButtonGroup>
            </DisclosureHeader>

            <DisclosurePanel>
              <CardView
                layout="waterfall"
                variant="quiet"
                size="M"
                selectionMode="none"
                aria-label="Assets variations"
                styles={style({
                  height: "full",
                  // marginX: -12,
                  // boxSizing: "border-box",
                })}
              >
                {assets.map((asset) => (
                  <Card key={asset.name} textValue={asset.name}>
                    <CardPreview>
                      <S3Asset
                        src={asset.src}
                        filePath={asset.filePath}
                        type={asset?.type}
                        color={asset?.color ?? asset?.value}
                        width={asset?.size?.width ?? 1}
                        height={asset?.size?.height ?? 1}
                        alt={asset.name}
                      />
                      <HoverState showOutline={false}>
                        <TooltipTrigger>
                          <ActionButton
                            aria-label={`Download ${asset.name}`}
                            size="M"
                            // staticColor="auto"
                            onPress={() => downloadAsset.mutate(asset)}
                            isPending={downloadAsset.isPending}
                            styles={style({
                              position: "absolute",
                              top: 8,
                              insetEnd: 8,
                            })}
                          >
                            <DownloadIcon />
                          </ActionButton>
                          <Tooltip>Download {asset.name}</Tooltip>
                        </TooltipTrigger>
                      </HoverState>
                    </CardPreview>
                  </Card>
                ))}
              </CardView>
            </DisclosurePanel>
          </Disclosure>
        </Skeleton>
      </div>
    </>
  );
};
